import type { TranslationKey } from '@/i18n-key';

import {
    CycleEvaluationStatus,
    type CycleEvaluationStatusValue,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import {
    CycleEvaluationGroupStatus,
    type CycleEvaluationGroupStatusValue,
} from '@/types/cycle-evaluation/CycleEvaluationGroup';

export type CycleStatusTone = 'draft' | 'active' | 'inactive' | 'completed';

const GROUP_STATUS_TONES: Readonly<Record<CycleEvaluationGroupStatusValue, CycleStatusTone>> = {
    [CycleEvaluationGroupStatus.DRAFT]: 'draft',
    [CycleEvaluationGroupStatus.ACTIVE]: 'active',
    [CycleEvaluationGroupStatus.DEACTIVATED]: 'inactive',
    [CycleEvaluationGroupStatus.COMPLETED]: 'completed',
};

const LEGACY_STATUS_TONES: Readonly<Record<CycleEvaluationStatusValue, CycleStatusTone>> = {
    [CycleEvaluationStatus.DRAFT]: 'draft',
    [CycleEvaluationStatus.ACTIVE]: 'active',
    [CycleEvaluationStatus.INACTIVE]: 'inactive',
};

const GROUP_STATUS_LABEL_KEYS: Readonly<Record<CycleEvaluationGroupStatusValue, TranslationKey>> = {
    [CycleEvaluationGroupStatus.DRAFT]: 'cycleEvaluationDashboard.status.draft',
    [CycleEvaluationGroupStatus.ACTIVE]: 'cycleEvaluationDashboard.status.active',
    [CycleEvaluationGroupStatus.DEACTIVATED]: 'cycleEvaluationDashboard.status.inactive',
    [CycleEvaluationGroupStatus.COMPLETED]: 'cycleEvaluationDashboard.status.completed',
};

const LEGACY_STATUS_LABEL_KEYS: Readonly<Record<CycleEvaluationStatusValue, TranslationKey>> = {
    [CycleEvaluationStatus.DRAFT]: 'cycleEvaluationDashboard.status.draft',
    [CycleEvaluationStatus.ACTIVE]: 'cycleEvaluationDashboard.status.active',
    [CycleEvaluationStatus.INACTIVE]: 'cycleEvaluationDashboard.status.inactive',
};

// Server data is cast into these types without runtime validation, so `status` can still fall
// outside the members below despite the type: the `in` checks in the getters are not dead code.
const UNKNOWN_STATUS_TONE: CycleStatusTone = 'inactive';
const UNKNOWN_STATUS_LABEL_KEY: TranslationKey = 'cycleEvaluationDashboard.status.inactive';

interface UnknownStatusWarning {
    context: string;
    status: string;
}

const warnUnknownStatus = ({ context, status }: UnknownStatusWarning): void => {
    console.error(
        `[performance-fe] Unrecognized cycle status "${status}" for ${context}; falling back to the inactive tone instead of crashing the list.`,
    );
};

export const getGroupStatusTone = (status: CycleEvaluationGroupStatusValue): CycleStatusTone => {
    if (Object.hasOwn(GROUP_STATUS_TONES, status)) {
        return GROUP_STATUS_TONES[status];
    }

    warnUnknownStatus({ context: 'a cycle evaluation group', status });

    return UNKNOWN_STATUS_TONE;
};

// A caller deciding whether a group is "already deactivated" must agree with the tone the row
// itself renders, unknown-status fallback included — otherwise a group the list shows as inactive
// can still be treated as active elsewhere.
export const isDeactivatedGroupStatus = (status: CycleEvaluationGroupStatusValue): boolean =>
    getGroupStatusTone(status) === 'inactive';

export const getLegacyStatusTone = (status: CycleEvaluationStatusValue): CycleStatusTone => {
    if (Object.hasOwn(LEGACY_STATUS_TONES, status)) {
        return LEGACY_STATUS_TONES[status];
    }

    warnUnknownStatus({ context: 'a legacy cycle evaluation', status });

    return UNKNOWN_STATUS_TONE;
};

export const getGroupStatusLabelKey = (status: CycleEvaluationGroupStatusValue): TranslationKey => {
    if (Object.hasOwn(GROUP_STATUS_LABEL_KEYS, status)) {
        return GROUP_STATUS_LABEL_KEYS[status];
    }

    return UNKNOWN_STATUS_LABEL_KEY;
};

export const getLegacyStatusLabelKey = (status: CycleEvaluationStatusValue): TranslationKey => {
    if (Object.hasOwn(LEGACY_STATUS_LABEL_KEYS, status)) {
        return LEGACY_STATUS_LABEL_KEYS[status];
    }

    return UNKNOWN_STATUS_LABEL_KEY;
};
